import type { AppConfig, ClientNameValue } from "./app-config.types";
import { DEFAULT_ORG_KEY } from "./resolve-organization";
import { getAppConfigFlag, initFlagsmith } from "./flagsmith";

export type AppConfigChangeListener = (config: AppConfig) => void;

const DEFAULT_RECAPTCHA_SITE_KEY = "6LdWEsIeAAAAABRpq2OEbJs1jljlw5bL_XXRwbW2";

const CLIENT_DEFAULTS: Record<
  string,
  Pick<AppConfig, "theme" | "phoneNumber" | "whatsappNumber">
> = {
  rapihogar: {
    theme: "theme-rapi-hogar",
    phoneNumber: "0810 555 2255",
    whatsappNumber: "11 4857 7777",
  },
  rus: {
    theme: "theme-rus",
    phoneNumber: "351 5760609",
    whatsappNumber: "11 5996 3708",
  },
  galicia: {
    theme: "theme-galicia",
    phoneNumber: "0800 555 2272",
    whatsappNumber: "11 3254 8281",
  },
  meridional: {
    theme: "theme-meridional",
    phoneNumber: "+5493515760671",
    whatsappNumber: "",
  },
  soelsac: {
    theme: "theme-soelsac",
    phoneNumber: "",
    whatsappNumber: "",
  },
};

export const AUTOLOGIN_FEATURE = "autologin";

function parseFeatures(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.filter((item): item is string => typeof item === "string");
}

export function isFeatureEnabled(feature: string): boolean {
  return getAppConfig().features.includes(feature);
}

export const defaultAppConfig: AppConfig = {
  clientName: "rapihogar",
  theme: CLIENT_DEFAULTS.rapihogar.theme,
  phoneNumber: CLIENT_DEFAULTS.rapihogar.phoneNumber,
  whatsappNumber: CLIENT_DEFAULTS.rapihogar.whatsappNumber,
  baseUrl: "",
  organizationId: "",
  tycEnabled: false,
  clientPortalUrl: "",
  recaptchaSiteKey: DEFAULT_RECAPTCHA_SITE_KEY,
  subscribeFcmUrl: "",
  pagoticApiUrl: "",
  legalDocumentsApiUrl: "",
  clarityId: "",
  features: [],
  sentry: {
    env: "",
    dsn: "",
    project: "portal",
    release: "0.0.0",
  },
};

let appConfig: AppConfig | null = null;
let currentHostname = DEFAULT_ORG_KEY;
const listeners = new Set<AppConfigChangeListener>();

export function hasAppConfig(): boolean {
  return appConfig !== null;
}

export function getAppConfig(): AppConfig {
  if (!appConfig) {
    throw new Error("App config has not been loaded");
  }

  return appConfig;
}

export function setAppConfig(config: AppConfig): AppConfig {
  appConfig = config;
  listeners.forEach((listener) => listener(config));
  return config;
}

export function resetAppConfig(): void {
  appConfig = null;
}

export function onAppConfigChange(
  listener: AppConfigChangeListener
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function clientPortalUrlFromEnv(): string {
  if (!import.meta.env.DEV) {
    return "";
  }

  return String(import.meta.env.VITE_CLIENT_PORTAL_URL || "");
}

function resolveClientPortalUrl(raw: unknown): string {
  const fromFlagsmith = String(raw ?? "").trim();
  return fromFlagsmith || clientPortalUrlFromEnv();
}

export function createEnvBoundConfig(): Pick<
  AppConfig,
  | "baseUrl"
  | "tycEnabled"
  | "recaptchaSiteKey"
  | "subscribeFcmUrl"
  | "pagoticApiUrl"
  | "legalDocumentsApiUrl"
  | "clarityId"
  | "sentry"
> {
  return {
    baseUrl: import.meta.env.VITE_BASE_URL || "",
    tycEnabled: import.meta.env.VITE_TYC_ENABLED === "true",
    recaptchaSiteKey:
      import.meta.env.VITE_RECAPTCHA_SITE_KEY || DEFAULT_RECAPTCHA_SITE_KEY,
    subscribeFcmUrl: import.meta.env.VITE_SUBSCRIBE_FCM_URL || "",
    pagoticApiUrl: import.meta.env.VITE_PAGOTIC_API_URL || "",
    legalDocumentsApiUrl: import.meta.env.VITE_LEGAL_DOCUMENTS_API_URL || "",
    clarityId: import.meta.env.VITE_APP_CLARITY_ID || "",
    sentry: {
      env: import.meta.env.VITE_SENTRY_ENV || "",
      dsn: import.meta.env.VITE_SENTRY_DSN || "",
      project: import.meta.env.VITE_SENTRY_PROJECT || "portal",
      release: import.meta.env.VITE_SENTRY_RELEASE || "0.0.0",
    },
  };
}

export function createFallbackConfigFromEnv(): AppConfig {
  const clientName = (import.meta.env.VITE_CLIENT_NAME ||
    "rapihogar") as ClientNameValue;
  const defaults = CLIENT_DEFAULTS[clientName] ?? CLIENT_DEFAULTS.rapihogar;

  return {
    clientName: clientName || "rapihogar",
    theme: defaults.theme,
    phoneNumber: defaults.phoneNumber,
    whatsappNumber: defaults.whatsappNumber,
    organizationId: String(import.meta.env.VITE_AUTOLOGIN_ORG_ID || ""),
    features: [],
    clientPortalUrl: resolveClientPortalUrl(""),
    ...createEnvBoundConfig(),
  };
}

export function parseOrganizationConfig(raw: unknown): AppConfig | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const data = raw as Record<string, unknown>;
  const clientName = String(data.clientName ?? "rapihogar") as ClientNameValue;
  const defaults = CLIENT_DEFAULTS[clientName] ?? CLIENT_DEFAULTS.rapihogar;

  return {
    clientName: clientName || "rapihogar",
    theme: String(data.theme ?? defaults.theme),
    phoneNumber: String(data.phoneNumber ?? defaults.phoneNumber),
    whatsappNumber: String(data.whatsappNumber ?? defaults.whatsappNumber),
    organizationId: String(data.organizationId ?? ""),
    features: parseFeatures(data.features),
    clientPortalUrl: resolveClientPortalUrl(data.clientPortalUrl),
    ...createEnvBoundConfig(),
  };
}

export function applyOrganizationFromFlags(hostname: string): AppConfig {
  const parsed = parseOrganizationConfig(getAppConfigFlag());

  if (!parsed) {
    throw new Error(`No config for hostname: ${hostname}`);
  }

  return setAppConfig(parsed);
}

export async function loadAppConfigFromFlagsmith(
  hostname: string,
  options: { allowEnvFallback?: boolean } = {}
): Promise<AppConfig> {
  currentHostname = hostname;
  const allowEnvFallback =
    import.meta.env.DEV && (options.allowEnvFallback ?? true);

  try {
    await initFlagsmith({
      hostname,
      onChange: () => {
        try {
          applyOrganizationFromFlags(currentHostname);
        } catch (error) {
          console.error("Failed to apply Flagsmith organization config", error);
        }
      },
    });

    return applyOrganizationFromFlags(hostname);
  } catch (error) {
    if (allowEnvFallback) {
      console.warn(
        "Flagsmith unavailable, using local environment fallback",
        error
      );
      return setAppConfig(createFallbackConfigFromEnv());
    }

    throw error;
  }
}
